/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {useContext, useEffect} from "react";
import {BaseComponent, ToastModule} from "@intuitionrobotics/thunderstorm/frontend";
import {AlertPanel} from "./ui/alerts/AlertPanel";
import {OnPatientDataUpdated, PatientsDataModule} from "@modules/PatientDataModule";
import {padding_rl_sm, Spacing} from "@styles/styles";
import {GraphPanelWrapper} from "./ui/graphs/GraphPanelWrapper";
import {AlertsModule, OnAlertsUpdated} from "@modules/AlertsModule";
import {currentTimeMillies, Second} from "@intuitionrobotics/ts-common";
import {hideTooltip} from "./widget/appTooltip";
import {LastInteractionPanel} from "./ui/LastInteractionPanel";
import {FallRiskPanel} from "./ui/FallRiskPanel";
import {VGap} from "./widget/VGap";
import {DB_Patient} from "@app/app-shared/patient";
import {useSelectedPatient} from "./PatientProvider";
import {PatientsContext} from "./ui/patients/PatientsContext";
import {SurveyStatusPanel} from "./ui/alerts/SurveyStatusPanel";
import {OrganizationsContext} from "./ui/organizations/OrganizationsContext";
import {ContactsContext} from "./ui/messages/ContactsContext";
import {PcpContactPanel} from "./ui/PCPContactPanel";

export const Page_Dashboard = () => {
    const selectedPatient = useSelectedPatient();
    if (!selectedPatient)
        return null;
    const {demo} = useContext(PatientsContext)
    useFetchData(selectedPatient, demo);
    return <Page_DashboardInner selectedPatient={selectedPatient} demo={demo}/>;
}

type Props = {
    selectedPatient: DB_Patient
    demo: boolean
};

class Page_DashboardInner
    extends BaseComponent<Props, {}>
    implements OnPatientDataUpdated, OnAlertsUpdated {

    private startingTime?: number;

    __onAlertsUpdated() {
        this.forceUpdate();
    }

    __onPatientDataUpdated() {
        this.forceUpdate();
    }

    private beforeUploadListener = () => {
        this.componentWillUnmount();
    };

    componentDidMount(): void {
        this.startingTime = currentTimeMillies();
        // Known issue: This is not working for all the "RECEIVED" alerts,
        // because the "AlertsModule.markAlertAsNotNew()" might not have time to finish.
        window.addEventListener("beforeunload", this.beforeUploadListener);
    }

    componentWillUnmount() {
        this.logDebug(`Page_Dashboard will unmount`);

        // Make sure that the new patient tooltip and the corresponding toast are closed.
        ToastModule.hideToast()
        hideTooltip();

        window.removeEventListener("beforeunload", this.beforeUploadListener);

        const selectedPatient = this.props.selectedPatient;
        if (!selectedPatient)
            return;

        if (currentTimeMillies() - this.startingTime! > Second && !this.props.demo)
            AlertsModule.markAlertAsNotNew(selectedPatient._id);
    }

    render() {
        const patient = this.props.selectedPatient;
        return <div
            className={`ll_h_s`}
            style={{marginLeft: -Spacing.s, marginRight: -Spacing.s, position: "relative"}}
        >

            {/* Left Column */}
            <div className={`ll_v_s ${padding_rl_sm}`} style={{flex: 2, maxWidth: "66.66%"}}>
                <GraphPanelWrapper patient={patient}/>
            </div>

            {/* Right  Column */}
            <div className={`ll_v_s ${padding_rl_sm}`} style={{flex: 1}}>
                <OrganizationsContext.Consumer>
                    {orgCtx => {
                        if (!orgCtx.organizations[patient.organizationId]?.name?.toLowerCase().includes("metroplus"))
                            return null;
                        if (this.props.demo)
                            return null;
                        return <>
                            <PcpContactPanel patient={patient}/>
                            <VGap/>
                        </>;
                    }}
                </OrganizationsContext.Consumer>
                <LastInteractionPanel patient={patient}/>
                <VGap/>
                <FallRiskPanel patient={patient}/>
                <VGap/>
                <AlertPanel patient={patient} demo={this.props.demo}/>
                <OrganizationsContext.Consumer>
                    {orgCtx => {
                        if (!orgCtx.organizations[patient.organizationId]?.name?.toLowerCase().includes("metroplus"))
                            return null;

                        return <ContactsContext.Consumer>
                            {contactsCtx => (
                                <SurveyStatusPanel patient={patient} contacts={contactsCtx.contacts} demo={this.props.demo}/>
                            )}
                        </ContactsContext.Consumer>
                    }}
                </OrganizationsContext.Consumer>
            </div>
        </div>;
    }

}

function useFetchData(selectedPatient: DB_Patient, demo: boolean) {
    useEffect(() => {
        if (demo)
            return;

        PatientsDataModule.fetchDataLazy(selectedPatient);
        PatientsDataModule.getLastUpdatedByPatient(selectedPatient.unitId);
        return () => PatientsDataModule.clearFetchingTimeout(selectedPatient.unitId)
    }, [selectedPatient.unitId, demo]);

    return null;
}

